// =============================================
// 1. FIXED TIME SPLIT UTILITIES WITH CORRECT COUNTING
// utils/timeSplitUtils.js
// =============================================

/**
 * Processes time entries to handle splits properly based on your database schema
 * @param {Array} entries - Array of time entries with time_entry_splits loaded
 * @returns {Array} - Expanded array with splits processed
 */
export const processTimeEntries = (entries) => {
  return entries.flatMap(entry => {
    // Check if entry has splits in the time_entry_splits table
    if (entry.is_split && entry.splits && entry.splits.length > 0) {
      return entry.splits.map((split, index) => ({
        ...entry,
        // Override with split-specific values
        duration_hours: split.duration_hours || 0,
        hourly_rate: split.hourly_rate || 0,
        is_billable: split.is_billable || false,
        description: split.description || entry.description,
        // Add split-specific fields
        split_id: split.id,
        split_index: index,
        is_split_entry: true,
        original_entry_id: entry.id,
        original_duration: entry.duration_hours,
        split_description: split.description || `Split ${index + 1}`,
        // Create unique ID for split entry
        id: `${entry.id}-split-${split.id}`,
        // Keep original task name but add split indicator
        task_name: `${entry.task_name} (${split.description || `Split ${index + 1}`})`,
        // Keep original timestamps but add split created timestamp
        split_created_at: split.created_at
      }))
    }
    return [{ ...entry, is_split_entry: false }]
  })
}

/**
 * Calculate correct entry counts
 * @param {Array} entries - Original entries array
 * @param {Array} expandedEntries - Expanded entries array with splits
 * @returns {Object} - Entry count statistics
 */
export const calculateEntryCounts = (entries, expandedEntries) => {
  const originalCount = entries.length
  const splitEntriesCount = entries.filter(entry => entry.is_split && entry.splits?.length > 0).length
  const totalSplitsCount = entries.reduce((sum, entry) => {
    return sum + (entry.is_split && entry.splits ? entry.splits.length : 0)
  }, 0)
  const expandedCount = expandedEntries.length
  
  return {
    original: originalCount,
    splitEntries: splitEntriesCount, // Number of original entries that have splits
    totalSplits: totalSplitsCount,   // Total number of individual splits
    expanded: expandedCount,         // Total after expansion
    addedBySplits: totalSplitsCount - splitEntriesCount // Net addition from splits
  }
}

/**
 * Calculates summary statistics from entries with split support
 * @param {Array} entries - Array of time entries (can include splits)
 * @returns {Object} - Summary statistics
 */
export const calculateSummaryWithSplits = (entries) => {
  const expandedEntries = processTimeEntries(entries)
  const counts = calculateEntryCounts(entries, expandedEntries)
  
  const summary = expandedEntries.reduce((acc, entry) => {
    acc.totalHours += entry.duration_hours || 0
    acc.totalEntries += 1
    
    if (entry.is_billable) {
      acc.billableHours += entry.duration_hours || 0
      acc.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
    }
    
    acc.uniqueUsers.add(entry.user_id)
    acc.uniqueClients.add(entry.client_id)
    acc.uniqueProjects.add(entry.project_id)
    
    return acc
  }, {
    totalHours: 0,
    billableHours: 0,
    revenue: 0,
    totalEntries: 0,
    uniqueUsers: new Set(),
    uniqueClients: new Set(), 
    uniqueProjects: new Set()
  })

  return {
    ...summary,
    ...counts,
    userCount: summary.uniqueUsers.size,
    clientCount: summary.uniqueClients.size,
    projectCount: summary.uniqueProjects.size,
    utilizationRate: summary.totalHours > 0 ? (summary.billableHours / summary.totalHours) * 100 : 0,
    avgRate: summary.billableHours > 0 ? summary.revenue / summary.billableHours : 0
  }
}
